import { hasPaymentMetadataOrders } from '@ca/common'
import type { PaymentMetadata } from '@ca/common'

export type GlobemedAttempt = {
  amount: number
  // ERP payment; kept after removal so a new amount updates the same payment
  paymentId: number | null
  // 'uncertain' may have reached ERP; never resend it. 'unclear' means ERP's
  // balance changed in a way that does not match the amount
  status: 'applied' | 'uncertain' | 'unclear' | 'removed'
  // The last request sent for this order set
  operation: 'apply' | 'remove'
  // ERP prepaid_amount before the request; null when ERP had no metadata yet
  baselinePrepaid: number | null
  createdAt: number
}

// Whether the last uncertain request took effect in ERP
export type GlobemedResolution = 'confirmed' | 'rejected' | 'unclear'

export const createGlobemedAttempt = ({
  amount,
  paymentId,
  status,
  operation,
  baselinePrepaid,
}: Omit<GlobemedAttempt, 'createdAt'>): GlobemedAttempt => ({
  amount,
  paymentId,
  status,
  operation,
  baselinePrepaid,
  createdAt: Date.now(),
})

// ERP answered and refused; anything else (timeouts, 5xx, network) is uncertain
const DEFINITE_REJECTIONS = new Set([
  'BAD_REQUEST',
  'UNAUTHORIZED',
  'FORBIDDEN',
  'NOT_FOUND',
  'METHOD_NOT_SUPPORTED',
  'CONFLICT',
  'PRECONDITION_FAILED',
  'PAYLOAD_TOO_LARGE',
  'UNPROCESSABLE_CONTENT',
  'TOO_MANY_REQUESTS',
])

export const isDefiniteRejection = (code: string | undefined) =>
  code !== undefined && DEFINITE_REJECTIONS.has(code)

const toMinorUnits = (value: number, decimals: number) =>
  Math.round(value * Math.pow(10, decimals))

// Applying GlobeMed moves orders to sale, so empty metadata means it did not land
const resolveApply = (
  attempt: Pick<GlobemedAttempt, 'amount' | 'baselinePrepaid'>,
  metadata: PaymentMetadata | null | undefined,
  decimals: number
): GlobemedResolution => {
  if (!hasPaymentMetadataOrders(metadata)) {
    return 'rejected'
  }
  const prepaid = toMinorUnits(metadata.prepaid_amount, decimals)
  const baseline = toMinorUnits(attempt.baselinePrepaid ?? 0, decimals)
  const amount = toMinorUnits(attempt.amount, decimals)

  if (prepaid - baseline === amount) {
    return 'confirmed'
  }
  if (attempt.baselinePrepaid !== null && prepaid === baseline) {
    return 'rejected'
  }
  return 'unclear'
}

// A removal lowers prepaid by the amount; the orders already have metadata
const resolveRemove = (
  attempt: Pick<GlobemedAttempt, 'amount' | 'baselinePrepaid'>,
  metadata: PaymentMetadata | null | undefined,
  decimals: number
): GlobemedResolution => {
  if (!hasPaymentMetadataOrders(metadata) || attempt.baselinePrepaid === null) {
    return 'unclear'
  }
  const prepaid = toMinorUnits(metadata.prepaid_amount, decimals)
  const baseline = toMinorUnits(attempt.baselinePrepaid, decimals)
  const amount = toMinorUnits(attempt.amount, decimals)

  if (baseline - prepaid === amount) {
    return 'confirmed'
  }
  if (prepaid === baseline) {
    return 'rejected'
  }
  return 'unclear'
}

export const resolveUncertainAttempt = (
  attempt: Pick<GlobemedAttempt, 'amount' | 'baselinePrepaid' | 'operation'>,
  metadata: PaymentMetadata | null | undefined,
  decimals: number
): GlobemedResolution =>
  attempt.operation === 'remove'
    ? resolveRemove(attempt, metadata, decimals)
    : resolveApply(attempt, metadata, decimals)

export type GlobemedNotice = 'notApplied' | 'notRemoved' | 'unclear'

const settleApply = (
  attempt: GlobemedAttempt,
  resolution: GlobemedResolution
): { attempt: GlobemedAttempt | null; notice: GlobemedNotice | null } => {
  if (resolution === 'confirmed') {
    return { attempt: { ...attempt, status: 'applied' }, notice: null }
  }
  if (resolution === 'unclear') {
    return { attempt: { ...attempt, status: 'unclear' }, notice: 'unclear' }
  }
  // A failed update leaves the earlier removed draft to update again
  const next =
    attempt.paymentId !== null
      ? { ...attempt, status: 'removed' as const }
      : null
  return { attempt: next, notice: 'notApplied' }
}

const settleRemove = (
  attempt: GlobemedAttempt,
  resolution: GlobemedResolution
): { attempt: GlobemedAttempt | null; notice: GlobemedNotice | null } => {
  if (resolution === 'confirmed') {
    return { attempt: { ...attempt, status: 'removed' }, notice: null }
  }
  if (resolution === 'rejected') {
    return { attempt: { ...attempt, status: 'applied' }, notice: 'notRemoved' }
  }
  return { attempt: { ...attempt, status: 'unclear' }, notice: 'unclear' }
}

// The state an uncertain attempt moves to once ERP metadata shows the outcome
export const settleUncertainAttempt = (
  attempt: GlobemedAttempt,
  resolution: GlobemedResolution
) =>
  attempt.operation === 'remove'
    ? settleRemove(attempt, resolution)
    : settleApply(attempt, resolution)
